import { HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, map, type Observable, of, throwError } from "rxjs";
import type { CreateCompanyPayload } from "./create-company.types";
import type { Company } from "./company.model";
import type {
	CompanyFieldMappingResponse,
	CompanyImportCheckResult,
	ImportDuplicateValidateItem,
	ImportDuplicateValidateResult,
} from "./company-import-export.types";
import {
	defaultExportFilename,
	normalizeFieldMappingResponse,
	parseFilenameFromContentDisposition,
	readHasImportPermission,
} from "./company-import-export.utils";
import { environment } from "../../../../environments/environment";
import { API_ENDPOINTS } from "../../constants/api-endpoints";
import { HttpService } from "../http/http.service";
import { isWorkspaceCurrencyId } from "@app/pages/settings/currency/services/currency.service";

/** Successful `GET company/export` — save {@link blob} via `URL.createObjectURL` / anchor download. */
export interface CompanyExportDownload {
	blob: Blob;
	filename: string;
}

/** PATCH `company/bulk-update` — `accountOwnerId` required (null clears owner). */
export interface BulkUpdateCompaniesPayload {
	companyIds: string[];
	accountOwnerId: string | null;
}

/**
 * Matches legacy B2B CRM `POST /api/company/all` body (e.g. b2b-crm.katdev.com / thread.katdev.com).
 */
export interface CompanyListCustomFieldFilter {
	fieldName: string;
	value?: string | number | boolean | string[] | null;
	from?: string | number | null;
	to?: string | number | null;
}

export interface CompanyListFilters {
	startDate: string | null;
	endDate: string | null;
	createdBy: string | null;
	searchQuery: string;
	employeeRange: string | string[] | null;
	industry?: string | string[] | null;
	country?: string | string[] | null;
	favouritesOnly?: boolean;
	/** Thread/OpenAPI list scope; legacy servers typically ignore unknown fields. */
	scope?: "all" | "mine";
	/** Advance Filter custom-field predicates (`POST company/all`). */
	customFields?: CompanyListCustomFieldFilter[];
}

export interface CompanyListRequest {
	pagination: { page: number; pageSize: number };
	sort: { field: string; orderby: "ASC" | "DESC" };
	filters: CompanyListFilters;
}

/**
 * Some company APIs treat explicit `null` filter fields as SQL `IS NULL`, which returns no rows.
 * Legacy B2B also accepts `sort.order` alongside `orderby`.
 */
function serializeCompanyListRequest(
	body: CompanyListRequest,
): Record<string, unknown> {
	const filters: Record<string, unknown> = {
		searchQuery: body.filters.searchQuery ?? "",
		scope: body.filters.scope ?? "all",
	};
	const optionalKeys: (keyof CompanyListFilters)[] = [
		"startDate",
		"endDate",
		"createdBy",
		"employeeRange",
		"industry",
		"country",
		"favouritesOnly",
		"customFields",
	];
	for (const key of optionalKeys) {
		const v = body.filters[key];
		if (v === null || v === undefined) {
			continue;
		}
		if (typeof v === "string" && v.trim() === "") {
			continue;
		}
		if (Array.isArray(v) && v.length === 0) {
			continue;
		}
		filters[key] = v;
	}
	return {
		pagination: body.pagination,
		sort: {
			field: body.sort.field,
			orderby: body.sort.orderby,
			order: body.sort.orderby,
		},
		filters,
	};
}

/**
 * Maps `PATCH /company/update/:id` body from the nested {@link Company} shape to flat entity keys
 * (the backend uses `Object.assign(company, dto)` on column names like `domainNamePrimaryLinkUrl`).
 */
const ADDRESS_FRAGMENT_TO_API: Record<string, string> = {
	addressStreet1: "addressAddressStreet1",
	addressStreet2: "addressAddressStreet2",
	addressCity: "addressAddressCity",
	addressState: "addressAddressState",
	addressCountry: "addressAddressCountry",
	addressPostcode: "addressAddressPostcode",
};

export function flattenCompanyPatchForApi(
	patch: Partial<Company> & Record<string, unknown>,
): Record<string, unknown> {
	const out: Record<string, unknown> = {};

	if (patch.name !== undefined) {
		out["name"] = patch.name;
	}
	if (patch.industry !== undefined) {
		out["industry"] = patch.industry;
	}
	if (patch.employeeRange !== undefined) {
		out["employeeRange"] = patch.employeeRange;
	}
	if (patch.idealCustomerProfile !== undefined) {
		out["idealCustomerProfile"] = patch.idealCustomerProfile;
	}
	if (patch.accountOwnerId !== undefined) {
		out["accountOwnerId"] = patch.accountOwnerId;
	}
	if (patch.logoUrl !== undefined) {
		out["logoUrl"] = patch.logoUrl;
	}

	if (patch.domainName !== undefined) {
		const d = patch.domainName;
		if (d?.primaryLinkUrl !== undefined) {
			out["domainNamePrimaryLinkUrl"] = d.primaryLinkUrl;
		}
		if (d?.primaryLinkLabel !== undefined) {
			out["domainNamePrimaryLinkLabel"] = d.primaryLinkLabel;
		}
	}

	if (patch.linkedinLink !== undefined) {
		const l = patch.linkedinLink;
		if (l?.primaryLinkUrl !== undefined) {
			out["linkedinLinkPrimaryLinkUrl"] = l.primaryLinkUrl;
		}
		if (l?.primaryLinkLabel !== undefined) {
			out["linkedinLinkPrimaryLinkLabel"] = l.primaryLinkLabel;
		}
	}

	if (patch.xLink !== undefined) {
		const x = patch.xLink;
		if (x?.primaryLinkUrl !== undefined) {
			out["xLinkPrimaryLinkUrl"] = x.primaryLinkUrl;
		}
		if (x?.primaryLinkLabel !== undefined) {
			out["xLinkPrimaryLinkLabel"] = x.primaryLinkLabel;
		}
	}

	if (patch.annualRecurringRevenue !== undefined) {
		const r = patch.annualRecurringRevenue;
		if (r?.amountMicros !== undefined) {
			out["annualRecurringRevenueAmountMicros"] = r.amountMicros;
		}
		if (r?.currencyCode !== undefined) {
			out["annualRecurringRevenueCurrencyCode"] = r.currencyCode;
		}
		if (r?.currencyId !== undefined && r.currencyId !== null) {
			const id = String(r.currencyId).trim();
			if (isWorkspaceCurrencyId(id)) {
				out["annualRecurringRevenueCurrencyId"] = id;
			}
		}
		if (r?.currencySymbol !== undefined) {
			out["annualRecurringRevenueCurrencySymbol"] = r.currencySymbol;
		}
	}

	if (patch.address !== undefined && patch.address !== null) {
		for (const [k, v] of Object.entries(patch.address)) {
			const apiKey = ADDRESS_FRAGMENT_TO_API[k];
			if (apiKey !== undefined && v !== undefined) {
				out[apiKey] = v;
			}
		}
	}

	if (patch.country !== undefined) {
		out["addressAddressCountry"] = patch.country;
	}
	if (patch.addressAddressCountry !== undefined) {
		out["addressAddressCountry"] = patch.addressAddressCountry;
	}

	/** Pass through custom-field name keys (and other plain extras) for merge on update. */
	const knownNestedOrMapped = new Set([
		"name",
		"industry",
		"employeeRange",
		"idealCustomerProfile",
		"accountOwnerId",
		"logoUrl",
		"domainName",
		"linkedinLink",
		"xLink",
		"address",
		"country",
		"addressAddressCountry",
		"annualRecurringRevenue",
	]);
	for (const [k, v] of Object.entries(patch as Record<string, unknown>)) {
		if (knownNestedOrMapped.has(k)) {
			continue;
		}
		if (v !== undefined) {
			out[k] = v;
		}
	}

	return out;
}

export type CompanyListSocialLink = {
	primaryLinkLabel: string;
	primaryLinkUrl: string;
	secondaryLinks: { label: string; url: string }[];
};

/** Flat row for the Angular companies UI (mapped from API `docs` items). */
export interface CompanySearchListItem {
	id: string;
	name: string;
	logo?: string | null;
	logoUrl?: string | null;
	domainName?: CompanyListSocialLink | null;
	linkedinLink?: CompanyListSocialLink | null;
	xLink?: CompanyListSocialLink | null;
	primaryEmail?: string | null;
	email?: string | null;
	primaryPhone?: string | null;
	phone?: string | null;
	locationLabel?: string | null;
	location?: string | null;
	linkedIn?: string | null;
	website?: string | null;
	industry?: string | null;
	employeeRange?: string | null;
	employees?: number | null;
	street1?: string | null;
	street2?: string | null;
	city?: string | null;
	state?: string | null;
	postcode?: string | null;
	country?: string | null;
	owner?: string | null;
	createdBy?: string | null;
	twitter?: string | null;
	idealCustomerProfile?: boolean;
	annualRecurringRevenue?: {
		amountMicros: number;
		currencyCode: string;
	} | null;
	status?: string | null;
	createdAt?: string | null;
	createdOn?: string | null;
	updatedAt?: string | null;
	isFavourite?: boolean;
	customFields?: unknown;
}

export interface CompanySearchResponse {
	items: CompanySearchListItem[];
	total: number;
}

export interface DeleteCompaniesResponse {
	message?: string;
}

/** `GET company/{id}/news` — stored RSS items for the company detail News tab. */
export interface CompanyNewsItem {
	id: string;
	companyId: string;
	title: string;
	link: string;
	source: string | null;
	snippet: string | null;
	publishedAt: string | null;
	createdAt: string;
}

/** Paginated `GET company/{id}/news` response. */
export interface CompanyNewsListResponse {
	docs: CompanyNewsItem[];
	total: number;
}

function normalizeCompanyNewsList(raw: unknown): CompanyNewsListResponse {
	if (!raw || typeof raw !== "object") {
		return { docs: [], total: 0 };
	}
	const o = raw as Record<string, unknown>;
	const rawDocs = Array.isArray(o["docs"])
		? o["docs"]
		: Array.isArray(o["items"])
			? o["items"]
			: [];
	const docs: CompanyNewsItem[] = rawDocs.map((row) => {
		const r = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
		return {
			id: typeof r["id"] === "string" ? r["id"] : "",
			companyId: typeof r["companyId"] === "string" ? r["companyId"] : "",
			title: typeof r["title"] === "string" ? r["title"] : "",
			link: typeof r["link"] === "string" ? r["link"] : "",
			source: typeof r["source"] === "string" ? r["source"] : null,
			snippet: typeof r["snippet"] === "string" ? r["snippet"] : null,
			publishedAt: typeof r["publishedAt"] === "string" ? r["publishedAt"] : null,
			createdAt: typeof r["createdAt"] === "string" ? r["createdAt"] : "",
		};
	});
	let total = 0;
	if (typeof o["total"] === "number") {
		total = o["total"];
	} else {
		total = docs.length;
	}
	return { docs, total };
}

/** Query params `pagination.page` / `pagination.pageSize` for company persons & leads tabs. */
export interface CompanyRelatedListPagination {
	page: number;
	pageSize: number;
	searchQuery?: string;
}

/** Normalized paginated list (legacy `docs` + `total`, or `items`). */
export interface CompanyRelatedListResponse {
	docs: unknown[];
	total: number;
}

function normalizeCompanyRelatedList(raw: unknown): CompanyRelatedListResponse {
	if (!raw || typeof raw !== "object") {
		return { docs: [], total: 0 };
	}
	const o = raw as Record<string, unknown>;
	const docs = Array.isArray(o["docs"])
		? o["docs"]
		: Array.isArray(o["items"])
			? o["items"]
			: [];
	let total = 0;
	if (typeof o["total"] === "number") {
		total = o["total"];
	} else {
		const pag = o["pagination"];
		if (pag && typeof pag === "object" && !Array.isArray(pag)) {
			const pt = (pag as Record<string, unknown>)["total"];
			if (typeof pt === "number") {
				total = pt;
			}
		}
		if (!total && typeof o["count"] === "number") {
			total = o["count"] as number;
		}
		if (!total) {
			total = docs.length;
		}
	}
	return { docs, total };
}

function asRecord(v: unknown): Record<string, unknown> | null {
	return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
}

function pickSocialLink(d: Record<string, unknown>, key: string): CompanyListSocialLink | null {
	const link = asRecord(d[key]);
	if (!link) {
		return null;
	}
	const label = typeof link["primaryLinkLabel"] === "string" ? link["primaryLinkLabel"].trim() : "";
	const url = typeof link["primaryLinkUrl"] === "string" ? link["primaryLinkUrl"].trim() : "";
	const secRaw = link["secondaryLinks"];
	const secondaryLinks = Array.isArray(secRaw)
		? secRaw
				.filter((item): item is { label: string; url: string } => {
					if (!item || typeof item !== "object") {
						return false;
					}
					const row = item as Record<string, unknown>;
					return typeof row["url"] === "string" || typeof row["label"] === "string";
				})
				.map((item) => ({
					label: typeof item.label === "string" ? item.label : "",
					url: typeof item.url === "string" ? item.url : "",
				}))
		: [];
	if (!label && !url && secondaryLinks.length === 0) {
		return null;
	}
	return {
		primaryLinkLabel: label,
		primaryLinkUrl: url,
		secondaryLinks,
	};
}

function pickLinkedIn(d: Record<string, unknown>): string {
	const link = pickSocialLink(d, "linkedinLink");
	if (link?.primaryLinkUrl) {
		return link.primaryLinkUrl;
	}
	if (link?.primaryLinkLabel) {
		return link.primaryLinkLabel;
	}
	const sec = link?.secondaryLinks?.[0]?.url;
	return typeof sec === "string" ? sec.trim() : "";
}

function pickXLink(d: Record<string, unknown>): string {
	const link = pickSocialLink(d, "xLink");
	if (link?.primaryLinkUrl) {
		return link.primaryLinkUrl;
	}
	if (link?.primaryLinkLabel) {
		return link.primaryLinkLabel;
	}
	const sec = link?.secondaryLinks?.[0]?.url;
	return typeof sec === "string" ? sec.trim() : "";
}

function pickText(v: unknown): string {
	return typeof v === "string" ? v.trim() : "";
}

function pickAddressPart(d: Record<string, unknown>, key: string): string {
	const a = asRecord(d["address"]);
	return pickText(a?.[key]);
}

function pickCreatedByName(d: Record<string, unknown>): string {
	const created = asRecord(d["createdBy"]);
	return pickText(created?.["name"]);
}

function pickAccountOwnerName(d: Record<string, unknown>): string {
	const owner = asRecord(d["accountOwner"]);
	if (!owner) {
		return "";
	}
	const name = pickText(owner["name"]);
	if (name) {
		return name;
	}
	const parts = [owner["nameFirstName"], owner["nameLastName"]].filter(
		(x): x is string => typeof x === "string" && !!x.trim(),
	);
	return parts.join(" ").trim();
}

function pickEmployees(d: Record<string, unknown>): number | null {
	const raw = d["employees"];
	if (typeof raw === "number" && Number.isFinite(raw)) {
		return raw;
	}
	if (typeof raw === "string" && raw.trim()) {
		const n = Number(raw);
		return Number.isFinite(n) ? n : null;
	}
	return null;
}

function pickDomainUrl(d: Record<string, unknown>): string {
	const dn = asRecord(d["domainName"]);
	const u = dn?.["primaryLinkUrl"];
	return typeof u === "string" ? u.trim() : "";
}

function pickDomain(d: Record<string, unknown>): string {
	const dn = asRecord(d["domainName"]);
	const label = dn?.["primaryLinkLabel"];
	if (typeof label === "string" && label.trim()) {
		return label.trim();
	}
	return pickDomainUrl(d);
}

function pickAddressLine(d: Record<string, unknown>): string {
	const a = asRecord(d["address"]);
	if (!a) return "";
	const city = a["addressCity"];
	const state = a["addressState"];
	const country = a["addressCountry"];
	const parts = [city, state, country].filter(
		(x): x is string => typeof x === "string" && !!x.trim(),
	);
	return parts.join(", ").trim();
}

/** Map one API company document → list row for cards and AG Grid. */
export function normalizeB2bCompanyDoc(raw: unknown): CompanySearchListItem {
	const d = asRecord(raw) ?? {};
	const id = typeof d["id"] === "string" ? d["id"] : "";
	const name = typeof d["name"] === "string" ? d["name"] : "";
	const createdAt =
		typeof d["createdAt"] === "string" ? d["createdAt"] : undefined;
	const updatedAt =
		typeof d["updatedAt"] === "string" ? d["updatedAt"] : undefined;
	const domain = pickDomain(d);
	const domainUrl = pickDomainUrl(d);
	const linkedIn = pickLinkedIn(d);
	const twitter = pickXLink(d);
	const location = pickAddressLine(d);
	const employeeRange =
		typeof d["employeeRange"] === "string" ? d["employeeRange"] : null;
	const logoUrl =
		typeof d["logoUrl"] === "string" && d["logoUrl"].trim()
			? d["logoUrl"].trim()
			: null;
	const domainName = pickSocialLink(d, "domainName") ?? {
		primaryLinkLabel: domain || "",
		primaryLinkUrl: domainUrl || "",
		secondaryLinks: [],
	};

	return {
		id,
		name,
		logoUrl,
		website: domainUrl || null,
		domainName,
		linkedinLink: pickSocialLink(d, "linkedinLink"),
		xLink: pickSocialLink(d, "xLink"),
		linkedIn,
		twitter,
		industry: pickText(d["industry"]) || null,
		employeeRange,
		employees: pickEmployees(d),
		street1: pickAddressPart(d, "addressStreet1") || null,
		street2: pickAddressPart(d, "addressStreet2") || null,
		city: pickAddressPart(d, "addressCity") || null,
		state: pickAddressPart(d, "addressState") || null,
		postcode: pickAddressPart(d, "addressPostcode") || null,
		country: pickAddressPart(d, "addressCountry") || null,
		owner: pickAccountOwnerName(d) || null,
		createdBy: pickCreatedByName(d) || null,
		idealCustomerProfile: d["idealCustomerProfile"] === true,
		locationLabel: location || employeeRange || null,
		location: location || null,
		annualRecurringRevenue: d["annualRecurringRevenue"] as {
			amountMicros: number;
			currencyCode: string;
		} | null,
		createdAt,
		createdOn: createdAt,
		updatedAt,
		isFavourite: d["isFavourite"] === true || d["isFavorite"] === true,
		customFields: d["customFields"],
		primaryEmail: null,
		email: "",
		primaryPhone: null,
		phone: "",
	};
}

function normalizeSearchResponse(raw: unknown): CompanySearchResponse {
	if (!raw || typeof raw !== "object") {
		return { items: [], total: 0 };
	}
	const o = raw as Record<string, unknown>;
	const rawItems = Array.isArray(o["items"])
		? o["items"]
		: Array.isArray(o["docs"])
			? o["docs"]
			: [];
	const items = rawItems.map((doc) => normalizeB2bCompanyDoc(doc));
	let total = 0;
	if (typeof o["total"] === "number") {
		total = o["total"];
	} else {
		const pag = asRecord(o["pagination"]);
		if (pag && typeof pag["total"] === "number") {
			total = pag["total"];
		} else if (typeof o["count"] === "number") {
			total = o["count"];
		} else {
			total = items.length;
		}
	}
	return { items, total };
}

@Injectable({ providedIn: "root" })
export class CompaniesService {
	private readonly http = inject(HttpService);

	/**
	 * `POST {apiUrl}/company/all` — same contract as legacy B2B CRM.
	 * `withCredentials: true` so `authTokenPair` (and refresh) cookies are sent to the API host.
	 */
	getCompanies(body: CompanyListRequest): Observable<CompanySearchResponse> {
		const path =
			(environment as { companiesSearchPath?: string }).companiesSearchPath ??
			API_ENDPOINTS.COMPANIES.LIST_ALL;
		const payload = serializeCompanyListRequest(body);
		return this.http
			.post<unknown>(path, payload, { withCredentials: true })
			.pipe(map(normalizeSearchResponse));
	}

	/** `POST {apiUrl}/company` — create company (legacy B2B / Thread body). */
	createCompany(
		body: CreateCompanyPayload,
	): Observable<Record<string, unknown>> {
		return this.http.post<Record<string, unknown>>(
			API_ENDPOINTS.COMPANIES.CREATE,
			body,
			{ withCredentials: true },
		);
	}

	/**
	 * `DELETE {apiUrl}/company` — JSON body `{ ids: string[] }`.
	 * Bearer (and workspace headers) still come from {@link AuthInterceptor} like `POST …/company/all`.
	 *
	 * `withCredentials: false` avoids browser CORS failure when the API answers with
	 * `Access-Control-Allow-Origin: *` (wildcard is invalid with credentialed cross-origin requests).
	 * If your host requires cookies on this route, fix CORS (echo `Origin` + `Allow-Credentials: true`) or use a dev proxy.
	 */
	deleteCompanies(ids: string[]): Observable<DeleteCompaniesResponse> {
		const trimmed = ids.map((id) => id?.trim()).filter((id) => !!id);
		if (trimmed.length === 0) {
			return throwError(
				() => new Error("deleteCompanies: missing or empty id(s)"),
			);
		}
		return this.http.delete<DeleteCompaniesResponse>(
			API_ENDPOINTS.COMPANIES.BULK_DELETE,
			{
				body: { ids: trimmed },
				withCredentials: false,
				headers: new HttpHeaders({ "Content-Type": "application/json" }),
			},
		);
	}

	/**
	 * `GET {apiUrl}/company/{id}` — same host as list/create; Bearer + cookies via {@link HttpService} + auth interceptor.
	 */
	getCompanyById(id: string): Observable<unknown> {
		const trimmed = id?.trim();
		if (!trimmed) {
			return throwError(
				() => new Error("getCompanyById: missing or empty id"),
			);
		}
		const path = `${API_ENDPOINTS.COMPANIES.DETAIL_BASE}/${encodeURIComponent(trimmed)}`;
		return this.http.get<unknown>(path, { withCredentials: true });
	}

	/**
	 * `GET {apiUrl}/company/{companyId}/persons?pagination.page=&pagination.pageSize=`
	 */
	getCompanyPersons(
		companyId: string,
		pagination: CompanyRelatedListPagination,
	): Observable<CompanyRelatedListResponse> {
		const trimmed = companyId?.trim();
		if (!trimmed) {
			return throwError(
				() => new Error("getCompanyPersons: missing or empty companyId"),
			);
		}
		const path = `${API_ENDPOINTS.COMPANIES.DETAIL_BASE}/${encodeURIComponent(trimmed)}/${API_ENDPOINTS.COMPANIES.DETAIL_PERSONS_SUFFIX}`;
		let params = new HttpParams()
			.set("pagination.page", String(pagination.page))
			.set("pagination.pageSize", String(pagination.pageSize));
		const search = pagination.searchQuery?.trim();
		if (search) {
			params = params.set("searchQuery", search);
		}
		return this.http
			.get<unknown>(path, { withCredentials: true, params })
			.pipe(map(normalizeCompanyRelatedList));
	}

	/**
	 * `GET {apiUrl}/company/{companyId}/leads?pagination.page=&pagination.pageSize=`
	 */
	/**
	 * `GET {apiUrl}/company/{companyId}/news?pagination.page=&pagination.pageSize=`
	 */
	getCompanyNews(
		companyId: string,
		pagination: CompanyRelatedListPagination,
	): Observable<CompanyNewsListResponse> {
		const trimmed = companyId?.trim();
		if (!trimmed) {
			return throwError(
				() => new Error("getCompanyNews: missing or empty companyId"),
			);
		}
		const path = `${API_ENDPOINTS.COMPANIES.DETAIL_BASE}/${encodeURIComponent(trimmed)}/${API_ENDPOINTS.COMPANIES.DETAIL_NEWS_SUFFIX}`;
		const params = new HttpParams()
			.set("pagination.page", String(pagination.page))
			.set("pagination.pageSize", String(pagination.pageSize));
		return this.http
			.get<unknown>(path, { withCredentials: true, params })
			.pipe(map(normalizeCompanyNewsList));
	}

	/**
	 * `POST {apiUrl}/company/{companyId}/news/sync` — re-fetch RSS; then call {@link getCompanyNews}.
	 */
	refreshCompanyNewsFromRss(companyId: string): Observable<{ ok: boolean }> {
		const trimmed = companyId?.trim();
		if (!trimmed) {
			return throwError(
				() => new Error("refreshCompanyNewsFromRss: missing or empty companyId"),
			);
		}
		const path = `${API_ENDPOINTS.COMPANIES.DETAIL_BASE}/${encodeURIComponent(trimmed)}/news/sync`;
		return this.http.post<{ ok: boolean }>(path, {}, { withCredentials: true });
	}

	getCompanyLeads(
		companyId: string,
		pagination: CompanyRelatedListPagination,
	): Observable<CompanyRelatedListResponse> {
		const trimmed = companyId?.trim();
		if (!trimmed) {
			return throwError(
				() => new Error("getCompanyLeads: missing or empty companyId"),
			);
		}
		const path = `${API_ENDPOINTS.COMPANIES.DETAIL_BASE}/${encodeURIComponent(trimmed)}/${API_ENDPOINTS.COMPANIES.DETAIL_LEADS_SUFFIX}`;
		let params = new HttpParams()
			.set("pagination.page", String(pagination.page))
			.set("pagination.pageSize", String(pagination.pageSize));
		const search = pagination.searchQuery?.trim();
		if (search) {
			params = params.set("searchQuery", search);
		}
		return this.http
			.get<unknown>(path, { withCredentials: true, params })
			.pipe(map(normalizeCompanyRelatedList));
	}

	/**
	 * `PATCH {apiUrl}/company/remove-person` — body `{ personId, type: "contact" | "lead" }`.
	 * Same Bearer + cookies as other company APIs.
	 */
	removePersonFromCompany(
		personId: string,
		type: "contact" | "lead",
	): Observable<unknown> {
		const trimmed = personId?.trim();
		if (!trimmed) {
			return throwError(
				() => new Error("removePersonFromCompany: missing or empty personId"),
			);
		}
		const path = API_ENDPOINTS.COMPANIES.REMOVE_PERSON;
		return this.http.patch<unknown>(
			path,
			{ personId: trimmed, type },
			{ withCredentials: true },
		);
	}

	/** `PATCH {apiUrl}/company/update/{id}` — partial update; body contains only changed fields. */
	updateCompany(
		id: string,
		payload: Partial<Company> & Record<string, unknown>,
	): Observable<unknown> {
		const trimmed = id?.trim();
		if (!trimmed) {
			return throwError(
				() => new Error("updateCompany: missing or empty id"),
			);
		}
		const path = `${API_ENDPOINTS.COMPANIES.UPDATE}/${encodeURIComponent(trimmed)}`;
		const body = flattenCompanyPatchForApi(payload);
		if (Object.keys(body).length === 0) {
			return throwError(
				() =>
					new Error(
						"updateCompany: payload flattened to an empty body; nothing would be persisted.",
					),
			);
		}
		return this.http.patch<unknown>(path, body, {
			withCredentials: true,
		});
	}

	/** PATCH `company/bulk-update` — update account owner for selected companies. */
	bulkUpdateCompanies(
		body: BulkUpdateCompaniesPayload,
	): Observable<{ updatedCount: number }> {
		return this.http.patch<{ updatedCount: number }>(
			API_ENDPOINTS.COMPANIES.BULK_UPDATE,
			body,
			{ withCredentials: true },
		);
	}

	/**
	 * `GET {apiUrl}/company/export` — Bearer + cookies via interceptor; file name from
	 * `Content-Disposition` or `Content-Type` fallback.
	 */
	exportCompanies(companyIds?: string[]): Observable<CompanyExportDownload> {
		const q: string[] = [];
		const ids = companyIds?.map((id) => id.trim()).filter(Boolean) ?? [];
		if (ids.length > 0) {
			q.push(`ids=${encodeURIComponent(ids.join(","))}`);
		}
		const url =
			q.length > 0
				? `${API_ENDPOINTS.COMPANIES.EXPORT}?${q.join("&")}`
				: API_ENDPOINTS.COMPANIES.EXPORT;
		return this.http
			.getBlobResponse(url, {
				withCredentials: true,
			})
			.pipe(
				map((res) => {
					const blob = res.body;
					if (!blob) {
						throw new Error("Export returned an empty body.");
					}
					const ct = res.headers.get("content-type");
					const fromCd = parseFilenameFromContentDisposition(
						res.headers.get("content-disposition"),
					);
					const filename = fromCd ?? defaultExportFilename(ct);
					return { blob, filename };
				}),
			);
	}

	/** `GET {apiUrl}/company/field-mapping` */
	getCompanyFieldMapping(): Observable<CompanyFieldMappingResponse> {
		return this.http
			.get<unknown>(API_ENDPOINTS.COMPANIES.FIELD_MAPPING, {
				withCredentials: true,
			})
			.pipe(map(normalizeFieldMappingResponse));
	}

	/**
	 * `POST {apiUrl}/upload/file` — `FormData` with field **`file`** (CSV / Excel). Server returns JSON whose
	 * rows are read via {@link normalizeUploadFileParsedResponse}.
	 */
	uploadCompanySpreadsheetFile(file: File): Observable<unknown> {
		const body = new FormData();
		body.append("file", file, file.name);
		return this.http.post<unknown>(API_ENDPOINTS.UPLOAD.FILE, body, {
			withCredentials: true,
		});
	}

	/** `POST upload/image/company-picture` — multipart `file`; returns `{ path, token }`. */
	uploadCompanyPicture(file: File): Observable<{ path: string; token?: string }> {
		const body = new FormData();
		body.append("file", file, file.name);
		return this.http.post<{ path: string; token?: string }>(
			API_ENDPOINTS.UPLOAD.COMPANY_PICTURE,
			body,
			{ withCredentials: true },
		);
	}

	/**
	 * `GET {apiUrl}/company/check-permissions?workspaceMemberId=&type=canImport`
	 * When the member id is missing, returns `{ allowed: true }` (no round-trip).
	 */
	checkCompanyImportPermission(
		workspaceMemberId: string,
	): Observable<CompanyImportCheckResult> {
		const trimmed = workspaceMemberId?.trim();
		if (!trimmed) {
			return of({ allowed: true });
		}
		const params = new HttpParams()
			.set("workspaceMemberId", trimmed)
			.set("type", "canImport");
		return this.http
			.get<unknown>(API_ENDPOINTS.COMPANIES.CHECK_IMPORT_PERMISSION, {
				withCredentials: true,
				params,
			})
			.pipe(
				map((raw) => ({
					allowed: readHasImportPermission(raw),
				})),
				catchError((err: unknown) => {
					if (!(err instanceof HttpErrorResponse)) {
						return of({ allowed: true });
					}
					if (err.status === 401) {
						return of({
							allowed: false,
							message:
								"Your session expired. Sign in again to import companies.",
						});
					}
					if (err.status === 403) {
						return of({
							allowed: false,
							message:
								"You do not have permission to import companies.",
						});
					}
					return of({ allowed: true });
				}),
			);
	}

	/** POST `company/validate-duplicates` — duplicate preview for import review step. */
	validateImportDuplicates(
		items: ImportDuplicateValidateItem[],
	): Observable<ImportDuplicateValidateResult> {
		return this.http.post<ImportDuplicateValidateResult>(
			API_ENDPOINTS.COMPANIES.VALIDATE_DUPLICATES,
			{ items },
			{ withCredentials: true },
		);
	}

	/**
	 * `POST {apiUrl}/company/import` — body is an array of row objects; keys must match API field
	 * names after column mapping. Optional query: `importId`, `workspaceMemberId`.
	 */
	importCompanies(
		rows: CreateCompanyPayload[],
		options?: {
			importId?: string;
			workspaceMemberId?: string;
			duplicateHandling?: "skip" | "update";
			/** Workspace member id assigned when a row payload has no owner. */
			accountOwnerId?: string | null;
		},
	): Observable<unknown> {
		let params = new HttpParams();
		const iid = options?.importId?.trim();
		const mid = options?.workspaceMemberId?.trim();
		const owner = options?.accountOwnerId?.trim();
		if (iid) {
			params = params.set("importId", iid);
		}
		if (mid) {
			params = params.set("workspaceMemberId", mid);
		}
		if (options?.duplicateHandling) {
			params = params.set("duplicateHandling", options.duplicateHandling);
		}
		if (owner) {
			params = params.set("accountOwnerId", owner);
		}
		const httpOpts: {
			withCredentials: true;
			params?: HttpParams;
		} = { withCredentials: true };
		if (params.keys().length > 0) {
			httpOpts.params = params;
		}
		return this.http.post<unknown>(
			API_ENDPOINTS.COMPANIES.IMPORT,
			rows,
			httpOpts,
		);
	}

	/** POST `company/favourite/:companyId` — add company to favourites. */
	addCompanyFavourite(companyId: string): Observable<{ isFavourite?: boolean }> {
		const id = companyId?.trim();
		if (!id) {
			return throwError(() => new Error("addCompanyFavourite: missing or empty companyId"));
		}
		const path = `${API_ENDPOINTS.COMPANIES.FAVOURITE_PREFIX}/${encodeURIComponent(id)}`;
		return this.http.post<{ isFavourite?: boolean }>(path, {}, { withCredentials: true });
	}

	/** DELETE `company/favourite/:companyId` — remove company from favourites. */
	removeCompanyFavourite(companyId: string): Observable<{ isFavourite?: boolean }> {
		const id = companyId?.trim();
		if (!id) {
			return throwError(() => new Error("removeCompanyFavourite: missing or empty companyId"));
		}
		const path = `${API_ENDPOINTS.COMPANIES.FAVOURITE_PREFIX}/${encodeURIComponent(id)}`;
		return this.http.delete<{ isFavourite?: boolean }>(path, { withCredentials: true });
	}
}
